import { AttachedFile } from 'src/app/entities';
import { ProofOfPayment } from '../models';

const RC_PREFIX = 'registro-compra/rc/';

export function normalizeProofOfPaymentId(value?: string | null): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    return '';
  }

  return normalized.startsWith(RC_PREFIX)
    ? normalized.slice(RC_PREFIX.length)
    : normalized;
}

export interface ProofOfPaymentUploadFile {
  id?: string | null;
  remoteId?: string | null;
  key?: string | null;
  name?: string | null;
  isProofOfPayment?: boolean;
  mailSent?: boolean;
  uploadedBy?: string;
  mailSentBy?: string;
  mailSentByEmail?: string;
  mailSentAt?: string;
  isDeleted?: boolean;
  toEmails?: string[];
}
/**
 * @description Mapea un array de `AttachedFile` a un array de `ProofOfPayment`, filtrando solo aquellos archivos que están marcados como comprobantes de pago y extrayendo su información relevante para construir el modelo de comprobante de pago utilizado en la aplicación.
 * - Se realiza un filtrado inicial para obtener solo los archivos que tienen la propiedad `isProofOfPayment` marcada como `true`, luego se extrae el `key` de cada archivo, se limpia de posibles prefijos específicos y se construye un objeto `ProofOfPayment` para cada archivo válido, asignando el `id` con el valor del `key` limpio y estableciendo explícitamente que se trata de un comprobante de pago mediante la propiedad `isProofOfPayment`.
 * @param files
 * @returns
 */
export function mapUploaderFilesToProofsOfPayment(
  files: AttachedFile[],
): ProofOfPayment[] {
  return files
    .filter((file) => file.isProofOfPayment)
    .map((file) => ({
      id: normalizeProofOfPaymentId(file.key),
      isProofOfPayment: true,
      mailSent: Boolean(file.mailSent),
      uploadedBy: file.uploadedBy,
      mailSentBy: file.mailSentBy,
      mailSentByEmail: file.mailSentByEmail,
      mailSentAt: file.mailSentAt,
      isDeleted: file.isDeleted,
      toEmails: file.toEmails,
    }))
    .filter((proof) => Boolean(proof.id));
}

/**
 * @description Mapea un array de `ProofOfPayment` a un array de `ProofOfPaymentUploadFile`, extrayendo la información relevante para cada comprobante de pago y formateándola adecuadamente para su uso en componentes de carga de archivos.
 * - Se filtran los comprobantes de pago que tienen un ID válido, se extrae el nombre del archivo a partir del ID y se construye un objeto `ProofOfPaymentUploadFile` para cada comprobante, marcando explícitamente que se trata de un comprobante de pago mediante la propiedad `isProofOfPayment`.
 * @param proofsOfPayment
 * @returns
 */
export function mapProofsOfPaymentToUploaderFiles(
  proofsOfPayment: ProofOfPayment[],
): ProofOfPaymentUploadFile[] {
  return proofsOfPayment
    .map((proof) => ({
      id: (proof.id ?? '').trim(),
      mailSent: Boolean(proof.mailSent),
      uploadedBy: proof.uploadedBy,
      mailSentBy: proof.mailSentBy,
      mailSentByEmail: proof.mailSentByEmail,
      mailSentAt: proof.mailSentAt,
      isDeleted: proof.isDeleted,
      toEmails: proof.toEmails,
    }))
    .filter((proof) => Boolean(proof.id))
    .map((proof) => ({
      remoteId: proof.id,
      key: proof.id,
      name:
        proof.id.split(';').pop() || proof.id.split('/').pop() || 'Comprobante',
      isProofOfPayment: true,
      mailSent: proof.mailSent,
      uploadedBy: proof.uploadedBy,
      mailSentBy: proof.mailSentBy,
      mailSentByEmail: proof.mailSentByEmail,
      mailSentAt: proof.mailSentAt,
      isDeleted: proof.isDeleted,
      toEmails: proof.toEmails,
    }));
}
